import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, ArrowRight, ArrowsClockwise, Pulse, MapPin, ChartLineUp, TrendUp, Lightning,
  Rocket, Bank, Globe, Handshake,
} from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const WORKFLOW_ICON = {
  evaluate_investment: TrendUp,
  launch_business: Rocket,
  create_holding: Bank,
  raise_capital: ChartLineUp,
  expand_internationally: Globe,
  structure_deal: Handshake,
};

export default function Radar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get(`/radar${refresh ? "?refresh=true" : ""}`);
      setData(data);
      setGenerating(data?.status === "generating");
    } catch {
      toast.error("Failed to load Radar");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  // Poll every 6s while generating
  useEffect(() => {
    if (!generating) return;
    const t = setInterval(() => { load(false); }, 6000);
    return () => clearInterval(t);
  }, [generating]);

  if (loading && !data) {
    return <div className="p-8 lg:p-12 text-overline text-muted-foreground" data-testid="radar-loading">Scanning Emerging Markets…</div>;
  }
  if (!data) return null;
  if (data.error) {
    return (
      <div className="p-8 lg:p-12 text-sm text-rose-500" data-testid="radar-error">
        Radar generation failed. <button onClick={() => load(true)} className="underline">Retry</button>
      </div>
    );
  }
  // First-time generation with no stale snapshot to serve
  if (data.status === "generating" && !data.headline) {
    return (
      <div className="p-8 lg:p-12 max-w-3xl" data-testid="radar-generating">
        <div className="text-overline text-primary mb-2">Opportunity intelligence</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">Orixa Radar is scanning Emerging Markets…</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          We are detecting emerging sectors, ranking markets and identifying named opportunities. This scan typically takes 60–90 seconds. The page will refresh automatically.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <ArrowsClockwise size={12} weight="bold" className="animate-spin" /> Live generation in progress
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="radar-page">
      <PageHeader
        eyebrow="Opportunity intelligence"
        title="Orixa Radar"
        subtitle="Detect emerging sectors, markets and named opportunities — each tied to a workflow you can launch in one click."
        action={
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary disabled:opacity-60"
            data-testid="radar-refresh"
          >
            <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh scan"}
          </button>
        }
      />

      {/* Headline */}
      {data.headline && (
        <div className="mt-8 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev" data-testid="radar-headline">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Live signal</div>
            {data.stale && (
              <div className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/15" data-testid="radar-refreshing-badge">
                <ArrowsClockwise size={10} weight="bold" className="inline mr-1 animate-spin" /> Refreshing scan…
              </div>
            )}
          </div>
          <div className="font-display text-xl sm:text-2xl tracking-tight leading-snug">{data.headline}</div>
          <div className="text-xs mt-3 opacity-70 font-mono">Generated {(data.generated_at || "").slice(0, 19).replace("T", " ")}</div>
        </div>
      )}

      {/* Sectors heatmap */}
      <div className="mt-12">
        <div className="text-overline text-primary mb-2">Sectors</div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-5">Emerging sector heatmap</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="radar-sectors">
          {(data.sectors || []).map((s) => {
            const heat = scoreColor(s.growth_score);
            const Icon = WORKFLOW_ICON[s.suggested_workflow] || Sparkle;
            return (
              <div key={s.name} className="bg-card border border-border rounded-xl p-5 flex flex-col" data-testid={`sector-${s.name}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display text-base tracking-tight">{s.name}</div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${heat.pill}`}>{s.momentum}</div>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <div className={`data-large text-3xl ${heat.text}`}>{s.growth_score}</div>
                  <div className="text-xs text-muted-foreground">/100 growth</div>
                </div>
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden mt-1 mb-3">
                  <div className={`h-full ${heat.bar}`} style={{ width: `${s.growth_score}%` }} />
                </div>
                {s.key_markets?.length > 0 && (
                  <div className="text-xs text-muted-foreground mb-2">
                    <MapPin size={11} weight="bold" className="inline mr-1" /> {s.key_markets.join(" · ")}
                  </div>
                )}
                <p className="text-xs leading-relaxed flex-1">{s.narrative}</p>
                {s.suggested_workflow && (
                  <Link
                    to={`/workflows/${s.suggested_workflow}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    data-testid={`sector-cta-${s.name}`}
                  >
                    <Icon size={12} weight="duotone" /> Launch workflow <ArrowRight size={10} weight="bold" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Markets ranking */}
      <div className="mt-14">
        <div className="text-overline text-primary mb-2">Markets</div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-5">Emerging market ranking</h2>
        <div className="border border-border rounded-xl overflow-x-auto bg-card" data-testid="radar-markets">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left p-3 text-overline text-muted-foreground">#</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Country</th>
                <th className="text-right p-3 text-overline text-muted-foreground">Score</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Trend</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Top sectors</th>
                <th className="text-left p-3 text-overline text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data.markets || []).map((m, i) => {
                const heat = scoreColor(m.composite_score);
                return (
                  <tr key={m.country} className="border-t border-border hover:bg-secondary/40 transition-colors">
                    <td className="p-3 text-xs text-muted-foreground font-mono">{String(i + 1).padStart(2, "0")}</td>
                    <td className="p-3 font-medium">{m.country}</td>
                    <td className={`p-3 text-right data-large text-lg ${heat.text}`}>{m.composite_score}</td>
                    <td className="p-3"><TrendBadge trend={m.trend} /></td>
                    <td className="p-3 text-xs text-muted-foreground">{(m.top_sectors || []).join(", ")}</td>
                    <td className="p-3">
                      {m.suggested_workflow && (
                        <Link to={`/workflows/${m.suggested_workflow}`} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                          Launch <ArrowRight size={11} weight="bold" />
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hotspots */}
      <div className="mt-14">
        <div className="text-overline text-primary mb-2">Hotspots</div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-5">Named investment opportunities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="radar-hotspots">
          {(data.hotspots || []).map((h) => {
            const heat = scoreColor(h.opportunity_score);
            const Icon = WORKFLOW_ICON[h.suggested_workflow] || Sparkle;
            return (
              <div key={h.name} className="bg-card border border-border rounded-xl p-5 flex flex-col" data-testid={`hotspot-${h.name}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-display text-lg tracking-tight leading-tight">{h.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{h.country} · {h.sector}</div>
                  </div>
                  <div className={`data-large text-2xl ${heat.text}`}>{h.opportunity_score}</div>
                </div>
                <p className="text-sm leading-relaxed mt-2 text-foreground/90">{h.thesis}</p>
                {h.ticket_range_usd && (
                  <div className="mt-3 text-xs font-mono text-muted-foreground">
                    Ticket · <span className="text-foreground font-semibold">{h.ticket_range_usd}</span>
                  </div>
                )}
                {h.key_players?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {h.key_players.slice(0, 4).map((p) => (
                      <span key={p} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-secondary text-foreground/80">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                {h.suggested_workflow && (
                  <Link
                    to={`/workflows/${h.suggested_workflow}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                    data-testid={`hotspot-cta-${h.name}`}
                  >
                    <Icon size={14} weight="duotone" /> Launch workflow <ArrowRight size={12} weight="bold" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Growth signals */}
      <div className="mt-14 mb-8">
        <div className="text-overline text-primary mb-2">Signals</div>
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-5">Growth signals</h2>
        <div className="space-y-3" data-testid="radar-signals">
          {(data.growth_signals || []).map((s, i) => (
            <div key={`${s.signal?.slice(0, 24) || i}`} className="bg-card border border-border rounded-xl p-5 flex items-start gap-4">
              <div className="h-9 w-9 bg-teal-soft text-teal rounded-md grid place-items-center flex-shrink-0">
                <Lightning size={16} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-sm">{s.signal}</div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${impactColor(s.impact).pill}`}>
                    {s.impact} impact
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Sectors: {(s.affected_sectors || []).join(", ")}  ·  Countries: {(s.affected_countries || []).join(", ")}
                </div>
                <p className="text-sm mt-2 leading-relaxed">{s.implication}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function scoreColor(score) {
  const s = Number(score) || 0;
  if (s >= 80) return { text: "text-teal", bar: "bg-teal", pill: "bg-teal-soft text-teal" };
  if (s >= 60) return { text: "text-primary", bar: "bg-primary", pill: "bg-secondary text-primary" };
  if (s >= 40) return { text: "text-gold", bar: "bg-gold", pill: "bg-gold-soft text-gold" };
  return { text: "text-muted-foreground", bar: "bg-muted-foreground/50", pill: "bg-secondary text-muted-foreground" };
}

function impactColor(impact) {
  if (impact === "High") return { pill: "bg-teal-soft text-teal" };
  if (impact === "Medium") return { pill: "bg-gold-soft text-gold" };
  return { pill: "bg-secondary text-muted-foreground" };
}

function TrendBadge({ trend }) {
  const map = {
    Improving: { pill: "bg-teal-soft text-teal", arrow: "↗" },
    Stable: { pill: "bg-secondary text-foreground/80", arrow: "→" },
    Deteriorating: { pill: "bg-rose-500/10 text-rose-500", arrow: "↘" },
  };
  const s = map[trend] || map.Stable;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${s.pill}`}>
      {s.arrow} {trend || "—"}
    </span>
  );
}
